/**
 * The sheet viewer's page layout ("Útlit síðu"), a device preference:
 *  - 'scroll' = Samfellt: every page in one continuous column, fitted to the width;
 *  - 'page'   = Heil síða: one whole page at a time, turned by the left / right third, swipes or keys;
 *  - 'twoUp'  = Tvær síður: two pages side by side — only while the viewer is landscape and
 *               ≥ 1000 CSS px wide (spreads.ts twoUpEligible).
 *
 * No preference of its own: the mode is derived from the existing kgr.viewer.fit ('width' | 'page')
 * and kgr.viewer.twoUp, so testers keep their settings and nothing is migrated. twoUp only applies
 * while two-up is possible and fit applies otherwise, so the portrait choice and the landscape
 * choice both survive rotation.
 *
 * A small external store (useSyncExternalStore) so the top-bar switch (PageModeToggle), the viewer's
 * double-tap and other browser tabs (storage events) stay in step. Kept in memory as well, so a
 * device that cannot write localStorage still switches for the session.
 */
import { useSyncExternalStore } from 'react';
import { PREF, readPref, writePref } from './storage';
import type { FitMode } from './types';

export type PageMode = 'scroll' | 'page' | 'twoUp';

export interface PageModePrefs {
  fit: FitMode;
  /** Two-up chosen for wide landscape (default true). */
  twoUp: boolean;
}

const isFit = (v: unknown): v is FitMode => v === 'width' || v === 'page';
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

/** The stored preferences (defaults: fit width, two-up chosen). */
export function readPageModePrefs(): PageModePrefs {
  return { fit: readPref(PREF.fit, 'width', isFit), twoUp: readPref(PREF.twoUp, true, isBool) };
}

/** The mode that applies: two-up when chosen and possible, else Heil síða or Samfellt by fit. */
export function effectiveMode(p: PageModePrefs, twoUpEligible: boolean): PageMode {
  if (p.twoUp && twoUpEligible) return 'twoUp';
  return p.fit === 'page' ? 'page' : 'scroll';
}

/** The preferences after choosing `mode` (pure): only what the choice is about changes. */
export function prefsForMode(p: PageModePrefs, mode: PageMode, twoUpEligible: boolean): PageModePrefs {
  if (mode === 'twoUp') return { ...p, twoUp: true };
  // Samfellt / Heil síða while two-up is possible also turns two-up off (else it would still
  // apply); in portrait only fit changes, so the landscape choice survives rotation.
  return { fit: mode === 'page' ? 'page' : 'width', twoUp: twoUpEligible ? false : p.twoUp };
}

/* ── store ─────────────────────────────────────────────────────────────── */

let current: PageModePrefs | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of [...listeners]) l();
}

function onStorage(e: StorageEvent): void {
  if (e.key !== null && e.key !== PREF.fit && e.key !== PREF.twoUp) return;
  const next = readPageModePrefs();
  if (current && current.fit === next.fit && current.twoUp === next.twoUp) return;
  current = next;
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (listeners.size === 1 && typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

/** The current preferences (same object until they change). */
export function getPageModePrefs(): PageModePrefs {
  return (current ??= readPageModePrefs());
}

function update(next: PageModePrefs): void {
  const prev = getPageModePrefs();
  if (next.fit !== prev.fit) writePref(PREF.fit, next.fit);
  if (next.twoUp !== prev.twoUp) writePref(PREF.twoUp, next.twoUp);
  if (next.fit === prev.fit && next.twoUp === prev.twoUp) return;
  current = next;
  emit();
}

/** The page-layout preferences; re-renders when they change (here, in another tab or by setPageMode). */
export function usePageModePrefs(): PageModePrefs {
  return useSyncExternalStore(subscribe, getPageModePrefs, getPageModePrefs);
}

/** Chooses a page layout (the "Útlit síðu" switch); see prefsForMode. */
export function setPageMode(mode: PageMode, twoUpEligible: boolean): void {
  update(prefsForMode(getPageModePrefs(), mode, twoUpEligible));
}

/** Double-tap in the middle of the sheet: Samfellt ↔ Heil síða. Returns the new fit. */
export function toggleWholePage(): FitMode {
  const p = getPageModePrefs();
  const fit: FitMode = p.fit === 'page' ? 'width' : 'page';
  update({ ...p, fit });
  return fit;
}
